import { Component, inject, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { DeliveryLocationsJeevesComponent } from 'gung-standard-jeeves';
import { first, forkJoin, mergeMap, of } from 'rxjs';
import { TyloCustomerUtilsService } from '../../../../services/utils/tylo-customer-utils.service';
import { environment } from './../../../../../environments/environment';
import { Customer } from 'gung-standard';

@Component({
    selector: 'tylo-delivery-locations-jeeves',
    templateUrl: './tylo-delivery-locations-jeeves.component.html',
    styleUrls: ['./tylo-delivery-locations-jeeves.component.css'],
    standalone: false
})
export class TyloDeliveryLocationsJeevesComponent extends DeliveryLocationsJeevesComponent implements OnInit {
  private tyloCustomerUtilsService = inject(TyloCustomerUtilsService);

  public formOh: FormGroup;
  public enableEditDeliveryAddress = false;
  isTylo = environment.projectName === 'tylo';
  isHelo = environment.projectName === 'helo';

  private userEmail;
  public isExportCustomer: boolean;
  public isPhoneMandatory = false;
  // private orderEmailConfirmation = 'order.se@tylohelo.com'; // Used if checkout.extra.oh.edit filled
  blockEditAddress: boolean = true;
  blockEditCountry: boolean = false;
  defaultPhoneNumber: string;
  deliveryMethod: number;
  shouldShowAddressInput: boolean = true;

  ngOnInit() {
    this.onNextBtnClicked.subscribe(_ => this.handleNextButtonClicked());
    this.selectedCustomerService
      .getSelectedCustomer()
      .pipe(
        first(),
        mergeMap(selectedCustomer =>
          forkJoin([
            of(selectedCustomer),
            this.deliveryLocationsService.getDeliveryLocationsForCustomer(selectedCustomer.id),
            this.authService.getCurrentUser().pipe(first())
          ])
        )
      )
      .subscribe(([selectedCustomer, deliveryLocations, user]) => {
        if(user.phone){
          this.defaultPhoneNumber = user.phone;
        }else if(selectedCustomer.extra?.fr?.comnr){
          this.defaultPhoneNumber = selectedCustomer.extra.fr.comnr
        }

        this.checkout.extra.isExportCustomer = this.tyloCustomerUtilsService.isSelectedCustomerExport(selectedCustomer);
        this.checkout.extra.customerVATCode = this.tyloCustomerUtilsService.getCustomerVATCode(selectedCustomer);

        this.selectedCustomer = selectedCustomer;
        
        this.shouldShowAddressField(selectedCustomer);
        
        const deliveryMethod = this.checkout.extra.procargs?.levsattkod || selectedCustomer.extra.kus.levsattkod;
        this.deliveryMethod = deliveryMethod;
        this.blockEditAddress = deliveryMethod == 3 || deliveryMethod == 64;
        if (this.isTylo) {
          // Swedish customers should not be able to change delivery to outside sweden
          // https://support.gung.se/a/tickets/344887
          this.blockEditCountry = !this.checkout.extra.isExportCustomer;
        }
        this.userEmail = user.email;
        this.initForm();
        this.deliveryLocations = [...deliveryLocations];

        if (this.selectedCustomer.extra.kus.ordlevplats1) {
          const deliveryLocation = this.deliveryLocations.find(location => {
            return location.extra.lp.ordlevplats1 === this.selectedCustomer.extra.kus.ordlevplats1;
          });
          this.deliveryLocationId = deliveryLocation.id;
        }
      });
  }

  public get deliveryLocationId(): string {
    return this.selectedDeliveryLocation ? this.selectedDeliveryLocation.id : '';
  }

  public set deliveryLocationId(deliveryLocationId: string) {
    // https://github.com/microsoft/TypeScript/issues/338
    // SHould not implement logic on setter, rather delegate it to method in order to allow overrides
    this.afterDeliveryLocationSet(deliveryLocationId);
  }

  protected handleNextButtonClicked(): void {
    if (this.form.invalid || this.formOh.invalid) {
      this.form.markAllAsTouched();
      this.formOh.markAllAsTouched();
      return;
    }

    this.assignFormValues2(this.form);
    const rawData = this.form.getRawValue();
    this.assignPostalCodeAndCity(rawData.ftgpostnr, rawData.ordlevadr4);
    this.stepDone.emit(this.checkout);
  }

  protected assignPostalCodeAndCity(postalCode: string, city: string): void {
    this.checkout.extra.procargs.ordlevadr4 = (city || '').trim();
    this.checkout.extra.procargs.ftgpostnr = (postalCode || '').trim();
  }

  private assignFormValues2(form: FormGroup) {
    const rawFormData = form.getRawValue();
    const rawformOhData = this.formOh.getRawValue();

    this.checkout.extra.oh = {
      ...this.checkout.extra.oh,
      ...rawformOhData
    };
    /* const emails = [rawformOhData.ta_mailnotified];
        if (!!this.formOh.get('edit').value) {
          emails.push(this.orderEmailConfirmation);
        }
        this.checkout.extra.confirmationEmails = emails; */
    // Check if user changed the delivery addres
    if (
      this.tyloCustomerUtilsService.isFreightConditional(this.selectedCustomer) &&
      ((this.selectedCustomer.extra.fr.ftgpostadr1 || '') !== rawFormData.ordlevadr2 ||
        (this.selectedCustomer.extra.fr.ftgpostadr2 || '') !== rawFormData.ordlevadr3 ||
        (this.selectedCustomer.extra.fr.ftglevpostnr || this.selectedCustomer.extra.fr.ftgpostnr || '') !==
        rawFormData.ftgpostnr ||
        (this.selectedCustomer.extra.fr.ftgpostadr3 || '') !== rawFormData.ordlevadr4 ||
        (this.selectedCustomer.extra.fr.landskod || '') !== rawFormData.ordlevadrlandskod)
    ) {
      this.checkout.extra._deliveryAddressEdited = true;
      this.checkout.extra.procargs.levvillkkod = 99;
    } else {
      this.checkout.extra._deliveryAddressEdited = false;
      this.checkout.extra.procargs.levvillkkod = this.selectedCustomer.extra.kus.levvillkkod;
    }

    if (rawformOhData.edit && (rawformOhData.edit as string).trim().length > 0) {
      if (!this.checkout.extra.confirmationEmails) {
        this.checkout.extra.confirmationEmails = [];
      }

      this.checkout.extra.confirmationEmails.push('tyloproshop.se@sauna360.com');
    }

    this.checkout.extra.procargs = {
      ...this.checkout.extra.procargs,
      ...rawFormData
    };
    console.log(this.checkout);
  }

  protected initForm() {
    const isPhoneMandatory = this.enableEditDeliveryAddress || this.deliveryMethod == 66 || this.deliveryMethod == 69;
    const isPhoneRequired = isPhoneMandatory ? Validators.required : Validators.nullValidator;
    const phoneValidators = isPhoneMandatory ? [Validators.required, Validators.pattern(/^[0-9-]*[0-9][0-9-]*$/)] : [];
    this.form = this.formBuilder.group({
      ordlevadr1: this.formBuilder.control(this.checkout.extra.procargs.ordlevadr1 || '', {
        validators: [Validators.required]
      }),
      ordlevadr2: this.formBuilder.control(this.checkout.extra.procargs.ordlevadr2 || ''),
      ordlevadr3: this.formBuilder.control(this.checkout.extra.procargs.ordlevadr3 || ''),
      ordlevadr4: this.formBuilder.control(this.checkout.extra.procargs.ordlevadr4 || ''),
      ftgpostnr: this.formBuilder.control(this.checkout.extra.procargs.ftgpostnr || ''),
      ordlevadrlandskod: this.formBuilder.control({
        value: this.checkout.extra.procargs.ordlevadrlandskod || '',
        disabled: this.blockEditCountry
      })
    });
    this.formOh = this.formBuilder.group({
      kundref2: this.formBuilder.control(this.checkout.extra.oh.kundref2 || ''),
      kundbestnr: this.formBuilder.control(this.checkout.extra.oh.kundbestnr || '', [Validators.required]),
      edit: this.formBuilder.control(this.checkout.extra.oh.edit || ''),
      godsmarke1: this.formBuilder.control(this.checkout.extra.oh.godsmarke1 || ''),
      q_oh_teleavisering: this.formBuilder.control(this.checkout.extra.oh.q_oh_teleavisering || this.defaultPhoneNumber || '', phoneValidators),
      ta_mailnotified: this.formBuilder.control(this.checkout.extra.oh.ta_mailnotified || this.userEmail || '', [
        Validators.email
      ])
    });
  }

  toggleEditDeliveryAddress(): void {
    this.enableEditDeliveryAddress = !this.enableEditDeliveryAddress;
    this.initForm();
  }

  protected afterDeliveryLocationSet(deliveryLocationId: string) {
    if (this.checkout.extra?.procargs?.ordlevplats1) {
      delete this.checkout.extra.procargs.ordlevplats1;
    }

    if (this.checkout.extra?.oh?.ordlevplats1) {
      delete this.checkout.extra.oh.ordlevplats1;
    }

    if (deliveryLocationId === 'custom') {
      this.enableEditDeliveryAddress = true;
      this.checkout.extra._gungEditedAddressFields = true;
      return;
    } else {
      this.enableEditDeliveryAddress = false;
      this.checkout.extra._gungEditedAddressFields = false;
    }

    if (deliveryLocationId === '') {
      if (this.selectedCustomer.extra?.fr?.ftgpostadr5) {
        this.checkout.extra.procargs = this.checkout.extra.procargs || {};
        this.checkout.extra.procargs.ordlevadr1 = this.selectedCustomer.extra.fr.ftgnamn;
        this.checkout.extra.procargs.ordlevadr2 = this.selectedCustomer.extra.fr.ordlevadr5;
        this.checkout.extra.procargs.ordlevadr3 = this.selectedCustomer.extra.fr.ftgpostadr5;
        this.checkout.extra.procargs.ordlevadr4 = this.selectedCustomer.extra.fr.ftgpostlevadr3;
        this.checkout.extra.procargs.ordlevadrlandskod = this.selectedCustomer.extra.fr.landskod;
        this.checkout.extra.procargs.ordlevadrprovincecode = this.selectedCustomer.extra.fr.provincecode;
        this.formOh.get('q_oh_teleavisering').setValue(this.selectedCustomer.extra.fr.comnr);
      } else {
        this.checkout.extra.procargs = this.checkout.extra.procargs || {};
        this.checkout.extra.procargs.ordlevadr1 = this.selectedCustomer.extra.fr.ftgnamn;
        this.checkout.extra.procargs.ordlevadr2 = this.selectedCustomer.extra.fr.ftgpostadr1;
        this.checkout.extra.procargs.ordlevadr3 = this.selectedCustomer.extra.fr.ftgpostadr2;
        this.checkout.extra.procargs.ordlevadrlandskod = this.selectedCustomer.extra.fr.landskod;
        this.formOh.get('q_oh_teleavisering').setValue(this.selectedCustomer.extra.fr.comnr);
      }

      this.postalCode = this.selectedCustomer.extra.fr.ftgpostnr;
      this.city =
        this.selectedCustomer.extra.fr.ftgpostlevadr3 || this.selectedCustomer.extra.fr.ftgpostadr3;

      this.assignPostalCodeAndCity(this.postalCode, this.city);

    } else {
      const newDeliveryLocation = this.deliveryLocations.find(location => location.id === deliveryLocationId);
      if (!newDeliveryLocation) {
        console.error(new Error(`Could not find delivery location: ${deliveryLocationId}`));
        return;
      }
      this.selectedDeliveryLocation = newDeliveryLocation;

      if (this.selectedDeliveryLocation.extra.fr.ftgpostadr5) {
        this.checkout.extra.procargs = this.checkout.extra.procargs || {};
        this.checkout.extra.procargs.ordlevadr1 = this.selectedDeliveryLocation.extra.fr.ftgnamn;
        this.checkout.extra.procargs.ordlevadr2 = this.selectedDeliveryLocation.extra.fr.ordlevadr5;
        this.checkout.extra.procargs.ordlevadr3 = this.selectedDeliveryLocation.extra.fr.ftgpostadr5;
        this.checkout.extra.procargs.ordlevadr4 = this.selectedDeliveryLocation.extra.fr.ftgpostlevadr3;
        this.checkout.extra.procargs.ordlevadrlandskod = this.selectedDeliveryLocation.extra.fr.landskod;
        this.checkout.extra.procargs.ordlevadrprovincecode = this.selectedDeliveryLocation.extra.fr.provincecode;
        this.formOh.get('q_oh_teleavisering').setValue(this.selectedDeliveryLocation.extra.fr.comnr);
        this.checkout.extra.oh.ordlevplats1	= this.selectedDeliveryLocation?.extra?.lp?.ordlevplats1;
        this.checkout.extra.procargs.ordlevplats1	= this.selectedDeliveryLocation?.extra?.lp?.ordlevplats1;
      } else {
        this.checkout.extra.procargs = this.checkout.extra.procargs || {};
        this.checkout.extra.procargs.ordlevadr1 = this.selectedDeliveryLocation.extra.fr.ftgnamn;
        this.checkout.extra.procargs.ordlevadr2 = this.selectedDeliveryLocation.extra.fr.ftgpostadr1;
        this.checkout.extra.procargs.ordlevadr3 = this.selectedDeliveryLocation.extra.fr.ftgpostadr2;
        this.checkout.extra.procargs.ordlevadrlandskod = this.selectedDeliveryLocation.extra.fr.landskod;
        this.formOh.get('q_oh_teleavisering').setValue(this.selectedDeliveryLocation.extra.fr.comnr);
        this.checkout.extra.oh.ordlevplats1	= this.selectedDeliveryLocation?.extra?.lp?.ordlevplats1;
        this.checkout.extra.procargs.ordlevplats1	= this.selectedDeliveryLocation?.extra?.lp?.ordlevplats1;
      }

      this.postalCode = this.selectedDeliveryLocation.extra.fr.ftgpostnr;
      this.city =
        this.selectedDeliveryLocation.extra.fr.ftgpostlevadr3 || this.selectedDeliveryLocation.extra.fr.ftgpostadr3;

      this.assignPostalCodeAndCity(this.postalCode, this.city);
    }

    const procargsKeys = Object.keys(this.form.getRawValue());

    const valueMap = {};
    procargsKeys.forEach(key => {
      valueMap[key] = this.checkout.extra.procargs[key] || '';
    });

    this.form.setValue(valueMap);
  }

  /**
   * If customer's momskod is 1, then don't show the regular Address field (different field from delivery address)
   * Per request from support: https://support.gung.se/a/tickets/622903
   */
  protected shouldShowAddressField(selectedCustomer: Customer) : void   {
    const customerMomsKod = selectedCustomer?.extra?.kus?.momskod;
    this.shouldShowAddressInput = customerMomsKod !== 1;
  }
}
